export interface MoodboardGridProps {
  images: string[];
  alt?: string;
}

/**
 * Responsive moodboard grid for 1–9 images.
 * Every tile is aspect-square (height derives from column width) so the
 * layout can never collapse or overflow, whatever the image count.
 * Visual variety comes from differing column counts per row.
 */
export function MoodboardGrid({ images, alt = 'Host photo' }: MoodboardGridProps) {
  const n = Math.min(images.length, 9);
  if (n === 0) return null;

  const tile = (src: string, i: number) => (
    <div key={i} className="aspect-square overflow-hidden rounded-xl bg-app-border">
      <img
        src={src}
        alt={`${alt} ${i + 1}`}
        loading="lazy"
        className="w-full h-full object-cover"
      />
    </div>
  );

  const row = (slice: string[], cols: string, offset: number) => (
    <div className={`grid ${cols} gap-2`}>
      {slice.map((s, i) => tile(s, offset + i))}
    </div>
  );

  // Single hero keeps a cinematic 16:9 frame.
  if (n === 1) return (
    <div className="aspect-video w-full overflow-hidden rounded-xl bg-app-border">
      <img src={images[0]} alt={`${alt} 1`} loading="lazy" className="w-full h-full object-cover" />
    </div>
  );

  // Each count is split into rows with distinct column counts for rhythm.
  const layouts: Record<number, Array<{ count: number; cols: string }>> = {
    2: [{ count: 2, cols: 'grid-cols-2' }],
    3: [{ count: 3, cols: 'grid-cols-3' }],
    4: [{ count: 2, cols: 'grid-cols-2' }, { count: 2, cols: 'grid-cols-2' }],
    5: [{ count: 2, cols: 'grid-cols-2' }, { count: 3, cols: 'grid-cols-3' }],
    6: [{ count: 3, cols: 'grid-cols-3' }, { count: 3, cols: 'grid-cols-3' }],
    7: [{ count: 3, cols: 'grid-cols-3' }, { count: 4, cols: 'grid-cols-4' }],
    8: [{ count: 4, cols: 'grid-cols-4' }, { count: 4, cols: 'grid-cols-4' }],
    9: [{ count: 3, cols: 'grid-cols-3' }, { count: 3, cols: 'grid-cols-3' }, { count: 3, cols: 'grid-cols-3' }],
  };

  const rows = layouts[n];
  let offset = 0;

  return (
    <div className="flex flex-col gap-2">
      {rows.map((r, idx) => {
        const slice = images.slice(offset, offset + r.count);
        const el = row(slice, r.cols, offset);
        offset += r.count;
        return <div key={idx}>{el}</div>;
      })}
    </div>
  );
}

export default MoodboardGrid;
